/**
 * @module modules/cluster-summary2-sk
 * @description <h2><code>cluster-summary2-sk</code></h2>
 *
 * @evt open-keys - An event that is fired when the user clicks the 'View on
 *     dashboard' button that contains the shortcut id, and the timestamp range of
 *     traces in the details that should be opened in the explorer, and the xbar
 *     location specified as a serialized cid.CommitID, for example:
 *
 *     {
 *       shortcut: 'X1129832198312',
 *       begin: 1476982874,
 *       end: 1476987166,
 *       xbar: {'offset':24750,'timestamp':1476985844},
 *     }
 *
 * @evt triaged - An event generated when the 'Update' button is pressed, which
 *     contains the new triage status. The detail contains the cid and triage
 *     status, for example:
 *
 *     {
 *       cid: {
 *         source: 'master',
 *         offset: 25004,
 *       },
 *       triage: {
 *         status: 'negative',
 *         messge: 'This is a regression in ...',
 *       },
 *     }
 *
 * @attr {Boolean} notriage - If true then don't display the triage controls.
 *
 * @example
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "ClusterSummary2Sk", {
    enumerable: true,
    get: function() {
        return ClusterSummary2Sk;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
require("../../../elements-sk/modules/collapse-sk");
require("../commit-detail-panel-sk");
require("../plot-google-chart-sk");
require("../triage2-sk");
require("../word-cloud-sk");
require("../commit-range-sk");
const _errorMessage = require("../errorMessage");
const _telemetry = require("../telemetry/telemetry");
const _json = require("../json");
const _csv = require("../csv");
const _plotgooglechartsk = require("../plot-google-chart-sk/plot-google-chart-sk");
const _index = require("../dataframe/index");
require("../window/window");
const _cid = require("../cid/cid");
const _aloginsk = require("../../../infra-sk/modules/alogin-sk/alogin-sk");
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
var _ClusterSummary2Sk;
// Oddly this seems to be the only way to retrieve the Intl locale for the
// browser.
const locale = Intl.NumberFormat().resolvedOptions().locale;
// These are different number formatters used in labelsForStepDetection.
const percentFormatter = Intl.NumberFormat(locale, {
    style: 'percent',
    maximumSignificantDigits: 4
}).format;
const decimalFormatter = Intl.NumberFormat(locale, {
    style: 'decimal',
    maximumSignificantDigits: 4
}).format;
const emptyFormatter = ()=>'';
/** Map each StepDetection to the labels and formatters used for that type. */ const labelsForStepDetection = {
    '': {
        regression: 'Regression Factor:',
        regressionFormatter: decimalFormatter,
        stepSize: 'Step Size:',
        stepSizeFormatter: decimalFormatter,
        lse: 'Least Squares Error:',
        lseFormatter: decimalFormatter
    },
    absolute: {
        regression: 'Absolute Change:',
        regressionFormatter: decimalFormatter,
        stepSize: '',
        stepSizeFormatter: emptyFormatter,
        lse: '',
        lseFormatter: emptyFormatter
    },
    const: {
        regression: 'Constant Threshhold:',
        regressionFormatter: decimalFormatter,
        stepSize: '',
        stepSizeFormatter: emptyFormatter,
        lse: '',
        lseFormatter: emptyFormatter
    },
    percent: {
        regression: 'Percentage Change:',
        regressionFormatter: percentFormatter,
        stepSize: '',
        stepSizeFormatter: emptyFormatter,
        lse: '',
        lseFormatter: emptyFormatter
    },
    percent_median: {
        regression: 'Percentage Change (Median):',
        regressionFormatter: percentFormatter,
        stepSize: '',
        stepSizeFormatter: emptyFormatter,
        lse: '',
        lseFormatter: emptyFormatter
    },
    cohen: {
        regression: 'Standard Deviations:',
        regressionFormatter: decimalFormatter,
        stepSize: '',
        stepSizeFormatter: emptyFormatter,
        lse: '',
        lseFormatter: emptyFormatter
    },
    mannwhitneyu: {
        regression: 'p:',
        regressionFormatter: percentFormatter,
        stepSize: '',
        stepSizeFormatter: emptyFormatter,
        lse: 'U:',
        lseFormatter: decimalFormatter
    },
    stepiness: {
        regression: 'Stepiness:',
        regressionFormatter: decimalFormatter,
        stepSize: 'Step Size:',
        stepSizeFormatter: decimalFormatter,
        lse: '',
        lseFormatter: emptyFormatter
    }
};
let ClusterSummary2Sk = (_ClusterSummary2Sk = class ClusterSummary2Sk extends _lit.LitElement {
    createRenderRoot() {
        return this;
    }
    renderBugLink() {
        if (!this.summary.notification_id) {
            return (0, _lit.html)``;
        }
        return (0, _lit.html)`
      <div>
        Bug:
        <a href="http://b/${this.summary.notification_id}">b/${this.summary.notification_id}</a>
      </div>
    `;
    }
    renderStatusPanel() {
        const statusClass = `${this.hiddenClass()} ${this.isEditor ? '' : 'disabled'}`;
        return (0, _lit.html)`
      <div id="status" class="${statusClass}">
        <p class="disabledMessage">You must be logged in to change the status.</p>
        <triage2-sk
          .value=${this.triageStatus.status}
          @change=${(e)=>{
            this.triageStatus = _object_spread_props(_object_spread({}, this.triageStatus), {
                status: e.detail
            });
        }}></triage2-sk>
        <input
          type="text"
          .value=${this.triageStatus.message}
          @change=${(e)=>{
            this.triageStatus = _object_spread_props(_object_spread({}, this.triageStatus), {
                message: e.currentTarget.value
            });
        }}
          label="Message" />
        <button class="action" @click=${this.updateStatus}>Update</button>
      </div>
    `;
    }
    render() {
        var _this_summary_step_fit, _this_summary_step_fit1, _this_frame_dataframe, _this_frame;
        return (0, _lit.html)`
      <div class="regression ${this.statusClass()}">
        ${this.labels.regression}
        <span>${this.labels.regressionFormatter(((_this_summary_step_fit = this.summary.step_fit) === null || _this_summary_step_fit === void 0 ? void 0 : _this_summary_step_fit.regression) || 0)}</span>
      </div>
      <div class="stats">
        <div class="labelled">
          Cluster Size:
          <span>${this.summary.num}</span>
        </div>
        ${this.leastSquares()}
        <div class="labelled">
          ${this.labels.stepSize}
          <span>${this.labels.stepSizeFormatter(((_this_summary_step_fit1 = this.summary.step_fit) === null || _this_summary_step_fit1 === void 0 ? void 0 : _this_summary_step_fit1.step_size) || 0)}</span>
        </div>
        ${this.renderBugLink()}
      </div>
      <div class="plot-wrapper">
        <plot-google-chart-sk
          specialevents
          .data=${this.graphData}
          .xbar=${this.xbarValue}
          @plot-data-select=${this.traceSelected}>
          <md-text slot="xbar">|</md-text>
        </plot-google-chart-sk>
      </div>
      ${this.renderStatusPanel()}
      <commit-detail-panel-sk
        selectable
        .trace_id=${this.summary.shortcut}
        .details=${this.commitsDetails}></commit-detail-panel-sk>
      <div class="actions">
        <button id="shortcut" @click=${this.openShortcut}>View on dashboard</button>
        <button @click=${this.toggleWordCloud}>Word Cloud</button>
        <button id="exportCsvBtn" ?disabled=${!this.hasTraces} @click=${this.exportCsv}>
          Export CSV
        </button>
        <a id="permalink" class=${this.hiddenClass()} href=${this.permaLink()}> Permlink </a>
        <commit-range-sk
          .trace=${this.summary.centroid || []}
          .commitIndex=${this.commitIndex}
          .header=${((_this_frame = this.frame) === null || _this_frame === void 0 ? void 0 : (_this_frame_dataframe = _this_frame.dataframe) === null || _this_frame_dataframe === void 0 ? void 0 : _this_frame_dataframe.header) || null}></commit-range-sk>
      </div>
      <collapse-sk class="wordCloudCollapse" .closed=${this.wordCloudClosedValue}>
        <word-cloud-sk .items=${this.summary.param_summaries2}></word-cloud-sk>
      </collapse-sk>
    `;
    }
    get hasTraces() {
        var _this_frame, _this_summary;
        const df = (_this_frame = this.frame) === null || _this_frame === void 0 ? void 0 : _this_frame.dataframe;
        if (!df || !df.header || df.header.length === 0) {
            return false;
        }
        const cleanDf = (0, _csv.removeSpecialTraces)(df);
        const hasTraceset = !!(cleanDf.traceset && Object.keys(cleanDf.traceset).length > 0);
        const hasCentroid = !!(((_this_summary = this.summary) === null || _this_summary === void 0 ? void 0 : _this_summary.centroid) && this.summary.centroid.length > 0);
        return hasTraceset || hasCentroid;
    }
    leastSquares() {
        var _this_summary_step_fit;
        return (0, _lit.html)`
      <div class="labelled">
        ${this.labels.lse}
        <span>${this.labels.lseFormatter(((_this_summary_step_fit = this.summary.step_fit) === null || _this_summary_step_fit === void 0 ? void 0 : _this_summary_step_fit.least_squares) || 0)}</span>
      </div>
    `;
    }
    connectedCallback() {
        super.connectedCallback();
        (0, _aloginsk.LoggedIn)().then((status)=>{
            this.isEditor = (status.roles || []).includes('editor');
        }).catch(_errorMessage.errorMessage);
    }
    updated(changedProperties) {
        super.updated(changedProperties);
    }
    updateDatasetStats(stepFit) {
        this.dataset.clustersize = this.summary.num.toString();
        if (stepFit) {
            this.dataset.steplse = stepFit.least_squares.toPrecision(2);
            this.dataset.stepsize = stepFit.step_size.toPrecision(2);
            this.dataset.stepregression = stepFit.regression.toPrecision(2);
        } else {
            this.dataset.steplse = '';
            this.dataset.stepsize = '';
            this.dataset.stepregression = '';
        }
    }
    buildGraphData(validHeaders) {
        const centroid = this.summary.centroid;
        if (!centroid || centroid.length === 0 || validHeaders.length !== centroid.length) {
            return;
        }
        const rows = centroid.map((value, i)=>{
            const header = validHeaders[i];
            const label = new Date(header.timestamp * 1000);
            return [
                header.offset,
                label,
                value
            ];
        });
        this.graphData = google.visualization.arrayToDataTable([
            [
                'Commit Position',
                'Date',
                'centroid'
            ],
            ...rows
        ]);
    }
    isInterestingStep(stepFit) {
        return !!stepFit && stepFit.status !== 'Uninteresting';
    }
    fetchCommitDetails(offset) {
        if (offset <= 0) {
            return;
        }
        ClusterSummary2Sk.lookupCids([
            offset
        ]).then((json)=>{
            this.commitsDetails = json.commitSlice || [];
        }).catch(_errorMessage.errorMessage);
    }
    updateXbarAndCommitDetails(stepFit, headers) {
        if (!this.isInterestingStep(stepFit) || !headers || !this.summary.step_point) {
            return;
        }
        const step = this.summary.step_point;
        const index = headers.findIndex((h)=>{
            var _h;
            return ((_h = h) === null || _h === void 0 ? void 0 : _h.offset) === step.offset;
        });
        if (index !== -1) {
            this.commitIndex = index;
        }
        this.xbarValue = step.offset;
        this.fetchCommitDetails(step.offset);
    }
    updateGraphData() {
        var _this_frame_dataframe, _this_frame;
        const stepFit = this.summary.step_fit;
        this.updateDatasetStats(stepFit);
        const headers = (_this_frame = this.frame) === null || _this_frame === void 0 ? void 0 : (_this_frame_dataframe = _this_frame.dataframe) === null || _this_frame_dataframe === void 0 ? void 0 : _this_frame_dataframe.header;
        const validHeaders = headers ? headers.filter((h)=>h !== null) : [];
        this.buildGraphData(validHeaders);
        this.updateXbarAndCommitDetails(stepFit, headers);
    }
    updateStatus() {
        const columnHeader = this.summary.step_point;
        if (!columnHeader) {
            return;
        }
        // Let's use standard CustomEvent constructor.
        this.dispatchEvent(new CustomEvent('triaged', {
            detail: {
                columnHeader,
                triage: this.triage
            },
            bubbles: true
        }));
    }
    openShortcut() {
        var _this_frame_dataframe, _this_frame;
        const header = (_this_frame = this.frame) === null || _this_frame === void 0 ? void 0 : (_this_frame_dataframe = _this_frame.dataframe) === null || _this_frame_dataframe === void 0 ? void 0 : _this_frame_dataframe.header;
        if (!header || !header.length || !this.summary.step_point) {
            return;
        }
        const firstHeader = header.find((h)=>h !== null);
        const lastHeader = [
            ...header
        ].reverse().find((h)=>h !== null);
        if (!firstHeader || !lastHeader) {
            return;
        }
        const detail = {
            shortcut: this.summary.shortcut,
            begin: firstHeader.timestamp,
            end: lastHeader.timestamp,
            xbar: this.summary.step_point
        };
        this.dispatchEvent(new CustomEvent('open-keys', {
            detail,
            bubbles: true
        }));
    }
    traceSelected(e) {
        var _this_frame_dataframe, _this_frame, _header_e_detail_tableRow, _header;
        const header = (_this_frame = this.frame) === null || _this_frame === void 0 ? void 0 : (_this_frame_dataframe = _this_frame.dataframe) === null || _this_frame_dataframe === void 0 ? void 0 : _this_frame_dataframe.header;
        const commitNumber = (_header = header) === null || _header === void 0 ? void 0 : (_header_e_detail_tableRow = _header[e.detail.tableRow]) === null || _header_e_detail_tableRow === void 0 ? void 0 : _header_e_detail_tableRow.offset;
        if (commitNumber === undefined) {
            return;
        }
        this.commitIndex = e.detail.tableRow;
        this.xbarValue = commitNumber;
        ClusterSummary2Sk.lookupCids([
            commitNumber
        ]).then((json)=>{
            this.commitsDetails = json.commitSlice || [];
        }).catch(_errorMessage.errorMessage);
    }
    toggleWordCloud() {
        this.wordCloudClosedValue = !this.wordCloudClosedValue;
    }
    hiddenClass() {
        return this.hasAttribute('notriage') ? 'hidden' : '';
    }
    permaLink() {
        // Bounce to the triage page, but with the time range narrowed to
        // contain just the step_point commit.
        if (!this.summary || !this.summary.step_point) {
            return '';
        }
        const begin = this.summary.step_point.timestamp;
        const end = begin + 1;
        return `/t/?begin=${begin}&end=${end}&subset=all`;
    }
    statusClass() {
        var _this_summary_step_fit;
        if (!this.summary) {
            return '';
        }
        const status = ((_this_summary_step_fit = this.summary.step_fit) === null || _this_summary_step_fit === void 0 ? void 0 : _this_summary_step_fit.status) || '';
        return status.toLowerCase();
    }
    /** @prop full_summary {string} A serialized:
   *
   *  {
   *    summary: cluster2.ClusterSummary,
   *    frame: dataframe.FrameResponse,
   *  }
   *
   */ get full_summary() {
        return this.fullSummary;
    }
    set full_summary(val) {
        if (!val) {
            return;
        }
        if (!val.frame || !val.summary) {
            (0, _errorMessage.errorMessage)('Invalid FullSummary object: missing frame or summary.', 0, {
                countMetricSource: _telemetry.CountMetric.ConfirmedRegressionInvalidPayload,
                source: 'cluster-summary2'
            });
            return;
        }
        const oldVal = this.fullSummary;
        this.fullSummary = val;
        this.summary = val.summary;
        this.frame = val.frame;
        this.updateGraphData();
        this.requestUpdate('full_summary', oldVal);
    }
    /** @prop triage {string} The triage status of the cluster.
   *     Something of the form:
   *
   *    {
   *      status: 'untriaged',
   *      message: 'This is a regression.',
   *    }
   *
   */ get triage() {
        return this.triageStatus;
    }
    set triage(val) {
        if (!val) {
            return;
        }
        const oldVal = this.triageStatus;
        this.triageStatus = val;
        this.requestUpdate('triage', oldVal);
    }
    get alert() {
        return this._alert;
    }
    set alert(val) {
        if (!val) {
            return;
        }
        const oldVal = this._alert;
        this._alert = val;
        this.labels = labelsForStepDetection[val.step] || labelsForStepDetection[''];
        this.requestUpdate('alert', oldVal);
    }
    constructor(){
        super();
        this.graphData = null;
        this.xbarValue = -1;
        this.commitIndex = -1;
        this.commitsDetails = [];
        this.wordCloudClosedValue = true;
        this.isEditor = false;
        this.frame = null;
        this.fullSummary = null;
        this._alert = null;
        this.labels = labelsForStepDetection[''];
        this.exportCsv = ()=>{
            var _this_frame, _this_plotGoogleChart;
            let df = (_this_frame = this.frame) === null || _this_frame === void 0 ? void 0 : _this_frame.dataframe;
            if (!df || !df.header || df.header.length === 0) {
                (0, _errorMessage.errorMessage)('No data available to export.');
                return;
            }
            const cleanDf = (0, _csv.removeSpecialTraces)(df);
            if (!cleanDf.traceset || Object.keys(cleanDf.traceset).length === 0) {
                if (this.summary.centroid && this.summary.centroid.length > 0) {
                    df = _object_spread_props(_object_spread({}, df), {
                        traceset: (0, _json.TraceSet)({
                            centroid: (0, _json.Trace)(this.summary.centroid)
                        })
                    });
                } else {
                    (0, _errorMessage.errorMessage)('No traces available to export.');
                    return;
                }
            } else {
                df = cleanDf;
            }
            if (((_this_plotGoogleChart = this.plotGoogleChart) === null || _this_plotGoogleChart === void 0 ? void 0 : _this_plotGoogleChart.selectedRange) && df.header && df.header.length > 0) {
                const selected = (0, _index.findSubDataframe)(df.header, this.plotGoogleChart.selectedRange, 'offset');
                if (selected.begin < selected.end) {
                    df = (0, _index.generateSubDataframe)(df, selected);
                }
            }
            const csvContent = (0, _csv.dataframeToCSV)(df);
            if (!csvContent) {
                (0, _errorMessage.errorMessage)('Failed to generate CSV from traces.');
                return;
            }
            const filename = `cluster-${this.summary.shortcut || Date.now()}.csv`;
            (0, _csv.downloadCSV)(filename, csvContent);
        };
        this.summary = {
            centroid: null,
            shortcut: '',
            step_point: null,
            notification_id: '',
            num: 0,
            step_fit: {
                regression: 0,
                least_squares: 0,
                step_size: 0,
                turning_point: 0,
                status: 'Uninteresting'
            },
            param_summaries2: [],
            ts: new Date().toISOString()
        };
        this.triageStatus = {
            status: '',
            message: ''
        };
    }
}, (()=>{
    /**
   * Look up the commit ids for the given offsets and sources.
   *
   * @param An array of CommitNumbers.
   * @returns A Promise that resolves the cids and returns an Array of serialized perfgit.Commit.
   */ _ClusterSummary2Sk.lookupCids = _cid.lookupCids;
})(), _ClusterSummary2Sk);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", typeof _json.ClusterSummary === "undefined" ? Object : _json.ClusterSummary)
], ClusterSummary2Sk.prototype, "summary", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", typeof _json.TriageStatus === "undefined" ? Object : _json.TriageStatus)
], ClusterSummary2Sk.prototype, "triageStatus", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Object)
], ClusterSummary2Sk.prototype, "graphData", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Number)
], ClusterSummary2Sk.prototype, "xbarValue", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Number)
], ClusterSummary2Sk.prototype, "commitIndex", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Array)
], ClusterSummary2Sk.prototype, "commitsDetails", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Boolean)
], ClusterSummary2Sk.prototype, "wordCloudClosedValue", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Boolean)
], ClusterSummary2Sk.prototype, "isEditor", void 0);
_ts_decorate([
    (0, _decorators.query)('plot-google-chart-sk'),
    _ts_metadata("design:type", typeof _plotgooglechartsk.PlotGoogleChartSk === "undefined" ? Object : _plotgooglechartsk.PlotGoogleChartSk)
], ClusterSummary2Sk.prototype, "plotGoogleChart", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Object,
        noAccessor: true
    }),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [])
], ClusterSummary2Sk.prototype, "full_summary", null);
_ts_decorate([
    (0, _decorators.property)({
        type: Object,
        noAccessor: true
    }),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [])
], ClusterSummary2Sk.prototype, "triage", null);
_ts_decorate([
    (0, _decorators.property)({
        type: Object,
        noAccessor: true
    }),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [])
], ClusterSummary2Sk.prototype, "alert", null);
ClusterSummary2Sk = _ts_decorate([
    (0, _decorators.customElement)('cluster-summary2-sk'),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [])
], ClusterSummary2Sk);
